import React, { useState, useEffect } from 'react';
import api from '../api';
import { Megaphone, X, Pin } from 'lucide-react';

/**
 * AnnouncementsBanner — sticky banner that displays announcements
 * visible to the current user. Pinned + unread items show at top.
 * User can dismiss (mark read) one at a time.
 *
 * Place once at the top of any dashboard layout.
 */
export const AnnouncementsBanner = () => {
  const [items, setItems] = useState([]);
  const [hidden, setHidden] = useState({}); // ann_id -> true after user closes

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await api.get('/announcements');
        if (mounted) setItems(res.data || []);
      } catch { /* silent */ }
    };
    load();
    // refresh every 60s while page is open
    const t = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  // Show only pinned-and-unread items by default (one banner at a time)
  const visibleNow = items.filter(a => a.pinned && a.unread && !hidden[a.id]);
  if (visibleNow.length === 0) return null;
  const a = visibleNow[0];

  const dismiss = async () => {
    setHidden(prev => ({ ...prev, [a.id]: true }));
    try { await api.post(`/announcements/${a.id}/read`); } catch { /* silent */ }
  };

  return (
    <div
      className="w-full px-4 py-3 bg-gradient-to-r from-amber-100 via-amber-50 to-blue-50 border-b-4 border-amber-500 flex items-start gap-3 sticky top-0 z-30"
      data-testid="announcement-banner"
    >
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold shadow-[3px_3px_0_0_#0f172a]">
        <Megaphone className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Pin className="w-3 h-3 text-amber-700" />
          <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">Announcement</span>
          <span className="text-[10px] text-slate-500">from {a.author_name}</span>
        </div>
        <h3 className="text-base font-bold text-slate-900 truncate" data-testid="announcement-banner-title">{a.title}</h3>
        <div className="text-sm text-slate-700 prose prose-sm max-w-none [&>p]:m-0 [&>p+p]:mt-1" dangerouslySetInnerHTML={{ __html: a.body_html }} />
      </div>
      <button
        onClick={dismiss}
        className="flex-shrink-0 w-7 h-7 rounded-full bg-white border-2 border-slate-900 hover:bg-slate-900 hover:text-white transition-colors flex items-center justify-center"
        title="Mark read and dismiss"
        data-testid="announcement-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default AnnouncementsBanner;
